import { ChevronDown, ChevronRight, ExternalLink, FileCode } from 'lucide-react';
import { useState } from 'react';
import RiskBadge from './RiskBadge';
import { cn } from '@/lib/utils';

type RiskLevel = 'critical' | 'high' | 'medium' | 'low' | 'info';

interface Vulnerability {
  id: string;
  title: string;
  severity: RiskLevel;
  description: string;
  impact?: string;
  remediation?: string;
  cwe?: string;
  file?: string;
  line?: number;
  snippet?: string;
  source?: 'sae' | 'dae' | 'aie' | 'gce' | 'zde' | 'rfia' | 'vge';
}

const SOURCE_LABELS: Record<string, { label: string; color: string }> = {
  sae: { label: 'Static', color: 'text-zinc-400 bg-zinc-800 border-zinc-700' },
  dae: { label: 'Dynamic', color: 'text-amber-400 bg-amber-950 border-amber-800' },
  aie: { label: 'AI', color: 'text-purple-400 bg-purple-950 border-purple-800' },
  gce: { label: 'Governance', color: 'text-blue-400 bg-blue-950 border-blue-800' },
  zde: { label: 'Zero-Day', color: 'text-red-400 bg-red-950 border-red-800' },
  rfia: { label: 'RFIA', color: 'text-cyan-400 bg-cyan-950 border-cyan-800' },
  vge: { label: 'VibeGuard', color: 'text-emerald-400 bg-emerald-950 border-emerald-800' },
};

interface VulnerabilityDetailProps {
  vulnerability: Vulnerability;
  forceExpanded?: boolean;
  /** Scan target URL (e.g. https://github.com/owner/repo) used to build file links */
  repoTarget?: string;
  /**
   * ID of the vulnerability currently highlighted by the parent (VulnerabilityGroup).
   * When this matches vulnerability.id the item expands and shows the highlight ring.
   * Scroll is handled by the parent — no event listener needed here.
   */
  highlightedVulnerabilityId?: string;
}

const VulnerabilityDetail = ({
  vulnerability,
  forceExpanded = false,
  repoTarget,
  highlightedVulnerabilityId,
}: VulnerabilityDetailProps) => {
  // Tracks the user's own toggle; parent's highlight is purely derived.
  const [userExpanded, setUserExpanded] = useState(forceExpanded);
  const [snippetOpen, setSnippetOpen] = useState(false);

  const isTargeted = highlightedVulnerabilityId === vulnerability.id;
  // Open when user toggled OR parent highlighted — pure derivation, no useEffect.
  const isExpanded = userExpanded || isTargeted;

  /** Build a github.com/…/blob/HEAD/file#Lline URL when we have enough info */
  const githubFileUrl =
    vulnerability.file && repoTarget && /github\.com/.test(repoTarget)
      ? `${repoTarget.replace(/\.git$/, '')}/blob/HEAD/${vulnerability.file}${
          vulnerability.line != null ? `#L${vulnerability.line}` : ''
        }`
      : null;

  return (
    <div
      id={`vuln-${vulnerability.id}`}
      className={cn(
        'border rounded-lg overflow-hidden transition-all duration-300',
        isExpanded ? 'shadow-md' : 'shadow-sm',
        isTargeted && 'ring-2 ring-primary ring-offset-2 ring-offset-background'
      )}
    >
      <button
        onClick={() => setUserExpanded(!isExpanded)}
        className="w-full flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors text-left"
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} vulnerability ${vulnerability.id}`}
      >
        {isExpanded ? (
          <ChevronDown className="w-5 h-5 text-muted-foreground" />
        ) : (
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        )}

        <code className="text-sm font-mono bg-muted px-2 py-1 rounded">{vulnerability.id}</code>

        <span className="flex-1 font-medium">{vulnerability.title}</span>

        {vulnerability.source && SOURCE_LABELS[vulnerability.source] && vulnerability.source !== 'sae' && (
          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${SOURCE_LABELS[vulnerability.source].color}`}>
            {SOURCE_LABELS[vulnerability.source].label}
          </span>
        )}
        <RiskBadge level={vulnerability.severity} />
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 border-t bg-muted/20 animate-fade-in">
          <div className="pt-4 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                Description
              </h4>
              <p className="text-sm">{vulnerability.description}</p>
            </div>

            {vulnerability.file && (
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm bg-muted/50 px-3 py-2 rounded-md font-mono">
                  <FileCode className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  {githubFileUrl ? (
                    <a
                      href={githubFileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-primary hover:underline"
                      title="View on GitHub"
                    >
                      {vulnerability.file}
                      {vulnerability.line != null ? `:${vulnerability.line}` : ''}
                      <ExternalLink className="w-3 h-3 inline-block ml-1 align-text-top" />
                    </a>
                  ) : (
                    <span className="flex-1">
                      {vulnerability.file}
                      {vulnerability.line != null ? `:${vulnerability.line}` : ''}
                    </span>
                  )}
                  {vulnerability.snippet && (
                    <button
                      onClick={() => setSnippetOpen((o) => !o)}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors flex-shrink-0 ml-1"
                      aria-expanded={snippetOpen}
                      aria-label={`${snippetOpen ? 'Hide' : 'Show'} code snippet`}
                    >
                      {snippetOpen ? (
                        <ChevronDown className="w-3 h-3" />
                      ) : (
                        <ChevronRight className="w-3 h-3" />
                      )}
                      snippet
                    </button>
                  )}
                </div>
                {vulnerability.snippet && snippetOpen && (
                  <pre className="text-xs bg-muted rounded-md p-3 overflow-x-auto leading-relaxed text-foreground/80 border border-muted-foreground/20">
                    <code>{vulnerability.snippet}</code>
                  </pre>
                )}
              </div>
            )}

            {vulnerability.impact && (
              <div>
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Impact
                </h4>
                <p className="text-sm">{vulnerability.impact}</p>
              </div>
            )}

            {vulnerability.remediation && (
              <div>
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Remediation
                </h4>
                <p className="text-sm bg-risk-low-bg border border-risk-low/20 p-3 rounded-lg">
                  {vulnerability.remediation}
                </p>
              </div>
            )}

            {vulnerability.cwe && (
              <div className="flex items-center gap-2 pt-2">
                <span className="text-xs text-muted-foreground">Reference:</span>
                <a
                  href={`https://cwe.mitre.org/data/definitions/${vulnerability.cwe.replace('CWE-', '')}.html`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                >
                  {vulnerability.cwe}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VulnerabilityDetail;
